import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import Subheader from "../../../components/ui/Subheader";
import { Select, Space } from "antd";
import {
  addKeyInArray,
  ArrayOption,
  selectSearchOption,
} from "../../../helpers/siteConfig";
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from "../../../store/store";
import useTable from "../../../hooks/useTable";
import {
  getAllAreaAsync,
  getAllRegionAsync,
} from "../../../store/features/NP/geoInfo/geoAPI";
import { ColumnsType } from "antd/es/table";
import { countParPageSerialNumber } from "../../../utils/number";
import { formatDate, formatTime } from "../../../utils/time";
import CustomTable from "../../../components/common/CustomTable";
import IconButton from "../../../components/common/IconButton";
import LeftArrowIcon from "../../../assets/icons/LeftArrowIcon";
import { useNavigate } from "react-router-dom";
import {
  GetALLPriceMartrixLogsParams,
  GetALLPriceMartrixParams,
  GetAllPriceMatrixData,
} from "../../../types/redux";
import {
  getAllPriceMatrixAsync,
  getAllPriceMatrixLogsAsync,
} from "../../../store/features/NP/payment/paymentAPI";
import { capitalize, formatAmount } from "../../../utils/string";
import useDebounce from "../../../hooks/useDebounce";
import Search from "antd/es/input/Search";

export default function PriceMatrixLogsList() {
  const [regionId, setRegionId] = useState<string | undefined>(undefined);
  const [areaId, setAreaId] = useState(undefined);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { page, limit, handlePagination, handleSetFirstPage, isFirstPage } =
    useTable();
  const { keyword, handleInputChange, handleEnterValue } = useDebounce();

  const { login } = useAppSelector((state) => state.login);
  const { regions, areas } = useAppSelector((state) => state.npGeo);
  const { allPriceMatrixLogs, loading, allPriceMatrixTotalLogs } =
    useAppSelector((state: RootState) => state.payment);

  useEffect(() => {
    dispatch(getAllRegionAsync({}));
  }, [dispatch]);

  useEffect(() => {
    if (regionId)
      dispatch(
        getAllAreaAsync({
          params: {
            region: regionId,
          },
        })
      );
  }, [dispatch, regionId]);

  const priceMatrixLogsData = useCallback(() => {
    const params: GetALLPriceMartrixLogsParams = {
      searchText: keyword,
      cluster: regionId,
      zone: areaId,
      limit: limit,
      skip: limit * (page - 1),
    };

    dispatch(
      getAllPriceMatrixLogsAsync({
        params,
      })
    );
  }, [dispatch, page, limit, regionId, areaId, keyword]);

  useEffect(() => {
    priceMatrixLogsData();
  }, [priceMatrixLogsData]);

  const reRender = () => {
    priceMatrixLogsData();
  };

  useEffect(() => {
    if (keyword && isFirstPage) {
      handlePagination(1, 10);
      handleSetFirstPage(false);
    }
  }, [handlePagination, handleSetFirstPage, isFirstPage, keyword]);

  const columns: ColumnsType<GetAllPriceMatrixData> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Cluster Name",
      dataIndex: "cluster",
      align: "center",
      render: (val) => (
        <div>{val?.regionName ? capitalize(val.regionName) : "N/A"}</div>
      ),
    },
    {
      title: "Zone Name",
      dataIndex: "zone",
      align: "center",
      render: (val) => (
        <div>{val?.areaName ? capitalize(val.areaName) : "N/A"}</div>
      ),
    },
    // {
    //   title: "Vendor",
    //   dataIndex: "vendor",
    //   align: "center",
    //   render: (val) => <div>{val?._id ? val._id : "N/A"}</div>,
    // },
    {
      title: "Vendor ID",
      dataIndex: "vendorId",
      align: "center",
      render: (val) => <div>{val || "N/A"}</div>,
    },
    {
      title: "Updated On",
      dataIndex: "timestamp",
      align: "center",
      render: (val: string) => (
        <div>{val ? `${formatDate(val)} - ${formatTime(val)}` : "N/A"}</div>
      ),
    },
    {
      title: "Effective Date",
      dataIndex: "changes",
      align: "center",
      render: (val) => (
        <div>{val?.effectiveDate ? formatDate(val.effectiveDate) : "N/A"}</div>
      ),
    },

    // Standard Columns
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Missed Call</span>
          <br />
          <span>Std_Line Item # 1</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => <div>{val?.standardMissedCallLineItem || "N/A"}</div>,
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Missed Call</span>
          <br />
          <span>Std_Missed call Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{formatAmount(val?.standardMissedCallAmount || 0)}</div>
      ),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Valid TT_Single PG</span>
          <br />
          <span>Std_Line Item # 2</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{val?.standardValidTTSinglePGLineItem || "N/A"}</div>
      ),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Valid TT_Single PG</span>
          <br />
          <span>Std_Valid TT Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{formatAmount(val?.standardValidTTSinglePGAmount || 0)}</div>
      ),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Valid TT_Double PG</span>
          <br />
          <span>Std_Line Item # 3</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{val?.standardValidTTDoublePGLineItem || "N/A"}</div>
      ),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Valid TT_Double PG</span>
          <br />
          <span>Std_Valid TT Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{formatAmount(val?.standardValidTTDoublePGAmount || 0)}</div>
      ),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_PGRH</span>
          <br />
          <span>Std_Line Item # 4</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => <div>{val?.standardMissedCallLineItem || "N/A"}</div>,
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_PGRH</span>
          <br />
          <span>Std_PGRH Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{formatAmount(val?.standardMissedCallAmount || 0)}</div>
      ),
    },

    // Premium Columns
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Missed Call</span>
          <br />
          <span>Prm_Line Item # 1</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => <div>{val?.premiumMissedCallLineItem || "N/A"}</div>,
      onHeaderCell: () => ({ className: "custom_edotco_tableYellow" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Missed Call</span>
          <br />
          <span>Prm_Missed call Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{formatAmount(val?.premiumMissedCallAmount || 0)}</div>
      ),
      onHeaderCell: () => ({ className: "custom_edotco_tableYellow" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Valid TT_Single PG</span>
          <br />
          <span>Prm_Line Item # 2</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{val?.premiumValidTTSinglePGLineItem || "N/A"}</div>
      ),
      onHeaderCell: () => ({ className: "custom_edotco_tableYellow" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Valid TT_Single PG</span>
          <br />
          <span>Prm_Valid TT Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{formatAmount(val?.premiumValidTTSinglePGAmount || 0)}</div>
      ),
      onHeaderCell: () => ({ className: "custom_edotco_tableYellow" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Valid TT_Double PG</span>
          <br />
          <span>Prm_Line Item # 3</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{val?.premiumValidTTDoublePGLineItem || "N/A"}</div>
      ),
      onHeaderCell: () => ({ className: "custom_edotco_tableYellow" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Valid TT_Double PG</span>
          <br />
          <span>Prm_Valid TT Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{formatAmount(val?.premiumValidTTDoublePGAmount || 0)}</div>
      ),
      onHeaderCell: () => ({ className: "custom_edotco_tableYellow" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_PGRH</span>
          <br />
          <span>Prm_Line Item # 4</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => <div>{val?.premiumPGRHLineItem || "N/A"}</div>,
      onHeaderCell: () => ({ className: "custom_edotco_tableYellow" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_PGRH</span>
          <br />
          <span>Prm_PGRH Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => <div>{formatAmount(val?.premiumPGRHAmount || 0)}</div>,
      onHeaderCell: () => ({ className: "custom_edotco_tableYellow" }),
    },

    // Contingency Columns
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Missed Call</span>
          <br />
          <span>Con_Line Item # 1</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => <div>{val?.contingencyMissedCallLineItem || "N/A"}</div>,
      onHeaderCell: () => ({ className: "custom_edotco_tableSky" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Missed Call</span>
          <br />
          <span>Con_Missed call Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{formatAmount(val?.contingencyMissedCallAmount || 0)}</div>
      ),
      onHeaderCell: () => ({ className: "custom_edotco_tableSky" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Valid TT_Single PG</span>
          <br />
          <span>Con_Line Item # 2</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{val?.contingencyValidTTSinglePGLineItem || "N/A"}</div>
      ),
      onHeaderCell: () => ({ className: "custom_edotco_tableSky" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Valid TT_Single PG</span>
          <br />
          <span>Con_Valid TT Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{formatAmount(val?.contingencyValidTTSinglePGAmount || 0)}</div>
      ),
      onHeaderCell: () => ({ className: "custom_edotco_tableSky" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Valid TT_Double PG</span>
          <br />
          <span>Con_Line Item # 3</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{val?.contingencyValidTTDoublePGLineItem || "N/A"}</div>
      ),
      onHeaderCell: () => ({ className: "custom_edotco_tableSky" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Valid TT_Double PG</span>
          <br />
          <span>Con_Valid TT Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{formatAmount(val?.contingencyValidTTDoublePGAmount || 0)}</div>
      ),
      onHeaderCell: () => ({ className: "custom_edotco_tableSky" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_PGRH</span>
          <br />
          <span>Con_Line Item # 4</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => <div>{val?.contingencyPGRHLineItem || "N/A"}</div>,
      onHeaderCell: () => ({ className: "custom_edotco_tableSky" }),
    },
    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_PGRH</span>
          <br />
          <span>Con_PGRH Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "changes",
      render: (val) => (
        <div>{formatAmount(val?.contingencyPGRHAmount || 0)}</div>
      ),
      onHeaderCell: () => ({ className: "custom_edotco_tableSky" }),
    },
  ];

  return (
    <>
      <AdminLayout title="Price Matrix Logs" headerTitle="Price Matrix Logs">
        <section className="mt-7">
          <Subheader
            title="Matrix Table"
            subTitle={``}
            subTitleClass="!text-primary"
          >
            <Space size="middle">
              <Search
                placeholder="Search by vendor id"
                onSearch={(val) => {
                  handleEnterValue(val);
                  handleSetFirstPage(true);
                }}
                onChange={(val) => {
                  handleInputChange(val);
                  handleSetFirstPage(true);
                }}
                className="custom-search-input"
              />

              <Select
                value={regionId}
                placeholder="Select cluster"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={ArrayOption(regions, "regionName", "_id", true)}
                onChange={(val) => {
                  setRegionId(val);
                  setAreaId(undefined);
                }}
                className="min-w-[120px]"
                disabled={login?.role === "region" || login?.role === "finance"}
              />
              <Select
                placeholder="Select zone"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={ArrayOption(areas, "areaName", "_id", true)}
                onChange={(val) => {
                  setAreaId(val);
                }}
                value={areaId}
                disabled={!regionId}
                className="min-w-[120px]"
              />

              <IconButton
                className="!border-others-header !text-sm !font-normal !rounded-lg !border !text-header !bg-transparent"
                type="default"
                icon={<LeftArrowIcon />}
                onClick={() => navigate(-1)}
              >
                <span>Back</span>
              </IconButton>
            </Space>
          </Subheader>

          <CustomTable
            loading={loading}
            total={allPriceMatrixTotalLogs}
            page={page}
            limit={limit}
            handlePagination={handlePagination}
            columns={columns}
            dataList={addKeyInArray(allPriceMatrixLogs)}
            className="po_manager_table"
            // isPagination={keyword ? false : true}
          />
        </section>
      </AdminLayout>
    </>
  );
}
